import { useEffect, useRef } from "react";
import { motion, useAnimationFrame, useMotionValue, useReducedMotion, useTransform } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

/** Fades + lifts its children into place the first time they scroll into view. */
export function Reveal({ children, delay = 0, y = 36, x = 0, className = "", ...rest }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, x }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 0.85, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** A heading whose words slide up out of a mask — the same move as the hero's poster cards. */
export function MaskTitle({ children, as: Tag = "h2", className = "" }) {
  const words = String(children).split(" ");
  return (
    <Tag className={className} aria-label={String(children)}>
      {words.map((w, i) => (
        <span key={i} aria-hidden="true" className="inline-block overflow-hidden align-bottom pb-[0.18em] -mb-[0.18em]">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, margin: "0px 0px -10% 0px" }}
            transition={{ duration: 0.9, delay: i * 0.07, ease: EASE }}
          >
            {w}
            {i < words.length - 1 ? " " : ""}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/** Endless ticker that drifts at one steady speed, whatever the page is doing. */
export function Marquee({ items, speed = 2.2, className = "" }) {
  const reduce = useReducedMotion();
  const baseX = useMotionValue(0);
  const wrapRef = useRef(null);
  const visible = useRef(true);

  // no point animating (and re-painting) a ticker nobody can see
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([entry]) => { visible.current = entry.isIntersecting; });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useAnimationFrame((_, delta) => {
    if (reduce || !visible.current || document.hidden) return;
    let next = baseX.get() - speed * (Math.min(delta, 64) / 1000);
    if (next <= -50) next += 50; // two identical halves → seamless loop
    baseX.set(next);
  });
  const x = useTransform(baseX, (v) => `${v}%`);

  const group = (k) => (
    <div key={k} className="flex flex-shrink-0 items-center" aria-hidden={k > 0 || undefined}>
      {items.map((it, i) => (
        <span key={i} className="flex items-center">
          <span className={`font-display font-black uppercase tracking-tighter whitespace-nowrap text-6xl md:text-8xl lg:text-9xl px-6 md:px-10 ${i % 2 ? "marquee-fill" : "text-outline"}`}>{it}</span>
          <span className="text-[#880015] text-3xl md:text-5xl">✦</span>
        </span>
      ))}
    </div>
  );

  return (
    <div ref={wrapRef} className={`overflow-hidden select-none ${className}`} aria-hidden="true">
      <motion.div className="flex w-max will-change-transform" style={{ x }}>
        {group(0)}
        {group(1)}
      </motion.div>
    </div>
  );
}
